import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { willItFit, installation, policies, trade } from '../../content/shopContent';
import { installTiers } from '../../content/products';
import { fmt, tierIncCents } from '../../lib/shopPricing';
import { Breadcrumbs, Html, PageHero } from '../../components/shop/ShopBits';
import usePageMeta from '../../hooks/usePageMeta';
import TradeQuoteForm from '../../components/shop/TradeQuoteForm';

// A guide as an accordion: the headings on screen, the words only when a heading is opened
// (Hugh, 17 Sep: less copy). The static build still carries every section for crawlers.
const Sections = ({ doc, open = [] }) => (
  <div className="max-w-3xl">
    <Html html={doc.intro} />
    <div className="border-t border-gray-200 mt-4" data-testid="guide-sections">
      {doc.sections.map((s) => (
        <details key={s.h2} className="group border-b border-gray-200 py-3" open={open.includes(s.h2) || undefined}>
          <summary className="cursor-pointer flex items-start justify-between gap-3 text-lg font-bold text-gray-900 select-none">
            <span>{s.h2}</span>
            <ChevronDown size={20} className="flex-shrink-0 text-gray-400 mt-1 transition-transform group-open:rotate-180" />
          </summary>
          <Html html={s.html} className="mt-3" />
        </details>
      ))}
    </div>
  </div>
);

// The fit guide is a guide only: no form, no photo ask. Photos are a step after payment,
// never before (Hugh, 17 Sep 2026); the one exception is the chooser's no-pick path, which
// has its own page (/shop/find-my-lock/photos).
export const WillItFitPage = () => {
  usePageMeta();
  return (
    <div className="min-h-screen bg-white">
      <PageHero title={willItFit.h1} sub="Work out your door type in five minutes, or let Find my lock do it in three taps.">
        <div className="mt-6">
          <Link to="/shop/find-my-lock">
            <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
              Find my lock <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </PageHero>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'Will it fit?' }]} />
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Sections doc={willItFit} />
          </div>
          <aside>
            <figure className="rounded-xl overflow-hidden border border-gray-200 mb-6">
              <img src="/images/work/narrow-mortice-aluminium-door.jpeg" alt="Narrow mortice lock in an aluminium door stile, fitted by Shore Doors" className="w-full" loading="lazy" />
              <figcaption className="text-xs text-gray-600 p-3">A narrow-stile aluminium door: the backset here is the distance from the door edge to the centre of the lock.</figcaption>
            </figure>
            <figure className="rounded-xl overflow-hidden border border-gray-200">
              <img src="/images/work/electronic-sliding-door-hook-lock.jpeg" alt="Electronic hook lock on a sliding door, fitted by Shore Doors" className="w-full" loading="lazy" />
              <figcaption className="text-xs text-gray-600 p-3">Sliding doors use a hook that drops into a box in the frame — measure that box for 28 or 35 mm.</figcaption>
            </figure>
          </aside>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Door type → what fits → install tier</h2>
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="p-3">Your door</th>
                <th className="p-3">Locks that fit</th>
                <th className="p-3">Usual install tier</th>
              </tr>
            </thead>
            <tbody>
              {willItFit.matrix.map((r) => (
                <tr key={r.door} className="border-t border-gray-200 align-top">
                  <td className="p-3 text-gray-900 font-medium">{r.door}</td>
                  <td className="p-3 text-gray-700">{r.fits}</td>
                  <td className="p-3 text-gray-700">{r.tier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const InstallationPage = () => {
  usePageMeta();
  return (
    <div className="min-h-screen bg-white">
      <PageHero title={installation.h1} sub="The fitted price before you pay, checked before we book.">
        <div className="mt-6">
          <Link to="/shop/digital-door-locks">
            <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
              Choose a lock <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </PageHero>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'Supply & install' }]} />
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {Object.values(installTiers).map((t) => (
            <div key={t.key} className="rounded-xl border border-red-200 bg-red-50 p-5" data-testid={`tier-${t.key}`}>
              <p className="flex items-center gap-2 font-bold text-gray-900">
                <Wrench size={18} className="text-red-600" /> {t.label}
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{fmt(tierIncCents(t.key))}</p>
              <p className="text-xs text-gray-600">incl. GST, per door, plus the lock (${t.exGst} + GST)</p>
              <p className="text-sm text-gray-700 mt-3">{t.blurb}</p>
            </div>
          ))}
        </div>
        <Link to="/shop/guides/fitting-a-digital-mortice-lock" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 mt-8 hover:border-gray-400" data-testid="installation-guide-link">
          <span className="text-gray-900 font-bold">A cut-in install in 22 photos</span>
          <span className="text-red-600 font-semibold inline-flex items-center gap-1">See the guide <ArrowRight size={16} /></span>
        </Link>
        <Sections doc={installation} />
      </div>
    </div>
  );
};

export const PolicyPage = ({ policyKey }) => {
  usePageMeta();
  const p = policies[policyKey];
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: p.h1 }]} />
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">{p.h1}</h1>
        <Html html={p.html} className="max-w-3xl" />
      </div>
    </div>
  );
};

export const TradePage = () => {
  usePageMeta();
  return (
    <div className="min-h-screen bg-white">
      <PageHero title={trade.h1} sub="Keypad locks, key safes, gate hardware and closers on account — one invoice per job." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'Trade' }]} />
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <Html html={trade.intro} />
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-4">
              <li>Trade pricing on multiples, quoted per job</li>
              <li>Supply-and-install across Auckland, codes handed over your way</li>
              <li>Accounts on the 20th of the month following invoice, by arrangement</li>
              <li>Purchases in trade are made under our shop terms (Consumer Guarantees Act exclusion for business buyers)</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Request a trade quote</h2>
            <TradeQuoteForm />
          </div>
        </div>
      </div>
    </div>
  );
};
